import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import FestivalOutlinedIcon from '@mui/icons-material/FestivalOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import HiveOutlinedIcon from '@mui/icons-material/HiveOutlined';
import LightModeIcon from '@mui/icons-material/LightMode';
import LockIcon from '@mui/icons-material/Lock';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { IconButton } from '@mui/material';
import { Dropdown, MenuProps, Popover, Tooltip } from 'antd';
import dummyAvatar from 'assets/imgs/dummy-avatar.jpg';
import clsx from 'clsx';
import ScrollRestoration from 'components/ScrollRestoration';
import SplashScreen from 'components/SplashScreen';
import { useGetMeQuery } from 'features/account/query';
import {
  darkModeSelector,
  fullContentViewStateSelector,
  sidebarCollapsedStateSelector,
} from 'features/appUI/selectors';
import {
  changeSidebarCollapsedState,
  setDarkMode,
  toggleFullContentView,
} from 'features/appUI/slice';
import { clearAuthToken } from 'features/auth/helpers';
import AppMainSidebar from 'layouts/MainLayout/AppMainSidebar';
import { useAppDispatch, useAppSelector } from 'lib/stores/hook';
import {
  cloneElement,
  memo,
  ReactElement,
  Suspense,
  useCallback,
  useMemo,
} from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { getAppModules } from 'routes/helpers';
import { history } from 'routes/history';
import { getPath } from 'routes/router-paths';
import { REGION } from 'utils/constants';
import { getAppLogo } from 'utils/getAppLogo';
import style from './MainLayout.module.scss';

const UPDATE_PASSWORD_MENU_KEY = 'userUpdatePasswordMenuItem';

const MainLayout = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const location = useLocation();

  history.navigate = navigate;
  history.location = location;

  const { data: getMeQueryResponse } = useGetMeQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { displayName } = getMeQueryResponse?.data || {};

  const isSidebarCollapsed = useAppSelector(sidebarCollapsedStateSelector);
  const isFullContentView = useAppSelector(fullContentViewStateSelector);
  const isDarkMode = useAppSelector(darkModeSelector);

  const userMenuItems = useMemo(
    (): MenuProps['items'] => [
      {
        label: 'Đổi mật khẩu',
        icon: <LockIcon />,
        key: UPDATE_PASSWORD_MENU_KEY,
      },
      {
        type: 'divider',
      },
      {
        label: 'Đăng xuất',
        icon: <LogoutOutlinedIcon />,
        key: 'logout',
        danger: true,
      },
    ],
    []
  );

  const onUserMenuClick = useCallback(
    ({ key }: { key: string }): MenuProps['onClick'] => {
      if (key === 'logout') {
        clearAuthToken();
        navigate(getPath('loginPage'));
        return;
      }
      key !== UPDATE_PASSWORD_MENU_KEY && navigate(key);
    },
    [navigate]
  );

  const apps = useMemo(
    (): Array<{ icon: ReactElement; label: string; url: string }> => [
      {
        icon: <MapsHomeWorkOutlinedIcon />,
        label: 'Khách sạn',
        url: getPath('hotelListPage'),
      },
      {
        icon: <MapOutlinedIcon />,
        label: 'Tour',
        url: getPath('tourListPage', REGION.DOMESTIC),
      },
      {
        icon: <FestivalOutlinedIcon />,
        label: 'Vé vui chơi',
        url: getPath('amusementListPage'),
      },
      { icon: <HiveOutlinedIcon />, label: 'Combo', url: '' },
    ],
    []
  );

  const content = useMemo(
    () => (
      <section className="max-h-100 w-60 overflow-y-auto">
        <ul className="grid grid-cols-2">
          {apps.map((item, index) => (
            <li
              key={index}
              className="cursor-pointer rounded-2xl p-3 text-center text-gray-600 transition-all hover:bg-gray-100"
              onClick={() => navigate(item.url)}
            >
              {cloneElement(item.icon, {
                className: 'text-3xl text-primary',
              })}
              <p className="text-sm">{item.label}</p>
            </li>
          ))}
        </ul>
      </section>
    ),
    [apps, navigate]
  );

  const { label: moduleLabel } = getAppModules();

  return (
    <div className="flex h-screen">
      <aside
        className={clsx(
          style.mainSidebarWrapper,
          isSidebarCollapsed && style.collapsed,
          'dark:border-r dark:border-[--border-color]'
        )}
      >
        <div className="mb-10 flex items-center justify-between px-5">
          <figure
            className="mb-0 cursor-pointer"
            onClick={() => navigate(getPath('portalPage'))}
          >
            <img src={getAppLogo()} alt="" className="max-h-8 max-w-9.5" />
          </figure>
          <Dropdown
            menu={{ items: userMenuItems, onClick: onUserMenuClick }}
            placement="bottomRight"
            overlayClassName="user-menu-dropdown"
            trigger={['click']}
          >
            <div className={style.userAvatarWrapper}>
              <AccountCircleOutlinedIcon />
            </div>
          </Dropdown>
        </div>
        <div className="mb-10 flex flex-col items-center justify-center text-center">
          <img
            src={dummyAvatar}
            alt=""
            className="mb-6 block h-24 w-24 rounded-full object-cover"
          />
          <div className="flex flex-col gap-1">
            <p className="text-sm text-white">{displayName ?? ''}</p>
          </div>
        </div>
        <AppMainSidebar />
      </aside>

      <main
        role="main"
        className={clsx(
          style.mainPageContentWrapper,
          isSidebarCollapsed && style.collapsed
        )}
      >
        <header
          className={clsx(
            style.fixedHeader,
            'dark:border-b-[--border-color] dark:bg-accent'
          )}
        >
          <Tooltip title={`${isSidebarCollapsed ? 'Hiển thị' : 'Ẩn'} sidebar`}>
            <IconButton
              onClick={() =>
                dispatch(changeSidebarCollapsedState(!isSidebarCollapsed))
              }
            >
              <MenuIcon className="dark:text-icon-dark" />
            </IconButton>
          </Tooltip>

          {moduleLabel && (
            <p className="ml-2 rounded-2xl bg-gray-200 px-2.5 py-1.5 text-xs">
              {moduleLabel}
            </p>
          )}

          <Popover
            content={content}
            trigger="click"
            placement="bottomRight"
            overlayStyle={{ padding: 0 }}
          >
            <IconButton className="ml-auto">
              <GridViewOutlinedIcon className="text-2xl dark:text-icon-dark" />
            </IconButton>
          </Popover>

          <Tooltip
            title={`${isFullContentView ? 'Thu hẹp' : 'Mở rộng'} hiển thị nội dung`}
          >
            <IconButton
              onClick={() =>
                dispatch(toggleFullContentView(!isFullContentView))
              }
            >
              {isFullContentView ? (
                <CloseFullscreenIcon className="text-xl dark:text-icon-dark" />
              ) : (
                <OpenInFullIcon className="text-xl dark:text-icon-dark" />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title={isDarkMode ? 'Bật Light mode' : 'Bật Dark mode'}>
            <IconButton onClick={() => dispatch(setDarkMode(!isDarkMode))}>
              {isDarkMode ? (
                <DarkModeIcon className="text-2xl dark:text-icon-dark" />
              ) : (
                <LightModeIcon className="text-2xl dark:text-icon-dark" />
              )}
            </IconButton>
          </Tooltip>
        </header>
        <section
          className={clsx(style.pageContentWrapper, 'dark:bg-accent')}
          id="main-layout"
        >
          <div
            className={clsx(
              'mx-auto px-8 pb-8',
              !isFullContentView && 'max-w-[144rem]'
            )}
          >
            <Suspense
              fallback={
                <ScrollRestoration>
                  <SplashScreen fullScreen />
                </ScrollRestoration>
              }
            >
              <Outlet />
            </Suspense>
          </div>
        </section>
      </main>
    </div>
  );
};

export default memo(MainLayout);
